import FadeIn from '@atlaskit/motion/fade-in';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { Text } from '@atlaskit/primitives/compiled/text';

import { useIntl } from 'react-intl';

import Image from '@atlaskit/image';

import lightModeAnimationSlim from '../assets/grouping-hierarchy-slim-light.gif';

import darkModeAnimationSlim from '../assets/grouping-hierarchy-slim-dark.gif';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { type JSX } from 'react/jsx-runtime';

import { type ReactNode, useCallback, useState } from 'react';

import { spotlightMessages } from './messages';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

const SPOTLIGHT_ELEMENT_ID = 'grouping-header-spotlight-target';

export const GroupingWithHierarchySpotlight = (): JSX.Element => {
	const { onAcknowledged, onClosed } = usePostOfficeUserInteractions();
	/**
	 * @atlaskit/spotlight requires the Target to be rendered as a sibling to the Spotlight component
	 * Therefore, we need to render a dummy target and use the portal to render the Spotlight component inside it
	 */
	const renderInline = useMountToHtmlElement({
		selectors: `#${SPOTLIGHT_ELEMENT_ID}`,
		as: 'portal',
		position: 'inside',
	});
	const { formatMessage } = useIntl();
	const [isVisible, setIsVisible] = useState<boolean>(true);
	const handleAcknowledge = useCallback(() => {
		setIsVisible(false);
		void onAcknowledged({ interactionIdentifier: 'okButton' });
	}, [onAcknowledged]);

	// The cross icon and click-outside/Escape are `closed`, not `acknowledged` — no choice was made.
	const handleClose = useCallback(() => {
		setIsVisible(false);
		void onClosed({ interactionIdentifier: 'close' });
	}, [onClosed]);

	return (
		<div>
			{renderInline(
				<PopoverProvider>
					<PopoverTarget>
						{/* Dummy target */}
						<div />
					</PopoverTarget>
					<PopoverContent
						dismiss={handleClose}
						placement="bottom-end"
						isVisible={isVisible}
						offset={[65, 10]}
					>
						{(() => {
							const spotlightCard = (
								<SpotlightCard>
									<SpotlightHeader>
										<SpotlightHeadline>
											{formatMessage(spotlightMessages.spotlightHeading)}
										</SpotlightHeadline>
										<SpotlightControls>
											<SpotlightDismissControl onClick={handleClose} autoFocus={false} />
										</SpotlightControls>
									</SpotlightHeader>
									<SpotlightMedia>
										{/* TODO: Add dark mode image */}
										<Image
											src={lightModeAnimationSlim}
											srcDark={darkModeAnimationSlim}
											alt={formatMessage(spotlightMessages.spotlightImageAltText)}
										/>
									</SpotlightMedia>
									<SpotlightBody>
										<Stack space="space.200">
											<Text>
												{formatMessage(spotlightMessages.spotlightSeeRelationshipsDescription)}
											</Text>
											<Text>
												{formatMessage(spotlightMessages.spotlightManageHierarchyDescription, {
													b: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
												})}
											</Text>
										</Stack>
									</SpotlightBody>
									<SpotlightFooter>
										<SpotlightActions>
											<SpotlightPrimaryAction onClick={handleAcknowledge}>
												{formatMessage(spotlightMessages.spotlightDismissButton)}
											</SpotlightPrimaryAction>
										</SpotlightActions>
									</SpotlightFooter>
								</SpotlightCard>
							);
							return fg('platform-dst-motion-uplift-spotlight') ? (
								spotlightCard
							) : (
								<FadeIn entranceDirection="right">
									{(props) => <div {...props}>{spotlightCard}</div>}
								</FadeIn>
							);
						})()}
					</PopoverContent>
				</PopoverProvider>,
			)}
		</div>
	);
};
